import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { formatStamp } from '@/lib/timeFields.jsx';
import { useVenueTz } from '@/lib/venueTz.js';

const RANGES = [
  { value: '1', label: '24h' },
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: 'all', label: 'All' },
];

function money(cents, currency = 'USD') {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
  } catch (_error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function pct(value) {
  return `${Number(value || 0).toFixed(1).replace(/\.0$/, '')}%`;
}

function time(value, tz) {
  return value ? formatStamp(value, tz) : '—';
}

function compact(value, length = 18) {
  if (!value) return '—';
  const text = String(value);
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-border rounded-md bg-card p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function EvidenceBadges({ evidence, compact: small = false }) {
  const contexts = evidence?.contexts || {};
  const edge = !!(contexts.host?.edge || contexts.widget?.edge);
  const badge = (label, active, title) => (
    <span title={title} className={`${small ? 'px-1 py-0 text-[9px]' : 'px-1.5 py-0.5 text-[10px]'} rounded font-medium ${active
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : 'bg-muted text-muted-foreground'}`}>{label}</span>
  );
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {badge('Host', !!contexts.host?.present, contexts.host?.present ? 'Host-page evidence collected' : 'Host-page evidence missing')}
      {badge('Widget', !!contexts.widget?.present, contexts.widget?.present ? 'Checkout-iframe evidence collected' : 'Checkout-iframe evidence missing')}
      {badge('CF', edge, edge ? 'Cloudflare sideband evidence collected' : 'Cloudflare sideband evidence missing')}
    </span>
  );
}

function FlagList({ flags }) {
  if (!flags?.length) return <span className="text-muted-foreground">None observed</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {flags.map((flag) => <span key={flag} className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px]">{flag}</span>)}
    </span>
  );
}

function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_error) { /* clipboard can be unavailable */ }
  };
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <code className="text-[11px] break-all">{value}</code>
      <button type="button" onClick={copy} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent flex-shrink-0">
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

function displayKey(key) {
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function displayValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map((v) => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function FieldGrid({ value }) {
  const rows = useMemo(() => {
    const output = [];
    const walk = (object, prefix = '') => {
      for (const [key, child] of Object.entries(object || {})) {
        const name = prefix ? `${prefix} · ${displayKey(key)}` : displayKey(key);
        if (child && typeof child === 'object' && !Array.isArray(child)) walk(child, name);
        else output.push([name, displayValue(child)]);
      }
    };
    walk(value);
    return output;
  }, [value]);
  if (!rows.length) return <div className="text-xs text-muted-foreground">No values collected.</div>;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
      {rows.map(([key, val]) => (
        <div key={key} className="min-w-0">
          <dt className="text-muted-foreground">{key}</dt>
          <dd className="break-words mt-0.5">{val}</dd>
        </div>
      ))}
    </dl>
  );
}

function DrawerSection({ title, children }) {
  return (
    <section className="border border-border rounded-md p-3 bg-card">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-2">{title}</h3>
      {children}
    </section>
  );
}

export function EvidenceDrawer({ tenantSlug, sessionId, onClose }) {
  const tz = useVenueTz();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let active = true;
    setDetail(null); setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/universe-widget/device-evidence/sessions/${encodeURIComponent(sessionId)}`)
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
        return body;
      })
      .then((body) => { if (active) setDetail(body); })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [tenantSlug, sessionId]);

  useEffect(() => {
    if (!sessionId) return undefined;
    const key = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [sessionId, onClose]);

  if (!sessionId) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button aria-label="Close evidence drawer" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative w-full sm:max-w-2xl h-full overflow-y-auto bg-background border-l border-border shadow-2xl p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Device evidence</h2>
            <div className="font-mono text-[11px] text-muted-foreground break-all">Session {sessionId}</div>
          </div>
          <button onClick={onClose} className="px-2 py-1 text-sm border border-input rounded hover:bg-accent">Close</button>
        </div>
        {error && <div className="text-sm text-destructive border border-destructive/30 rounded p-3">{error}</div>}
        {!detail && !error && <div className="text-sm text-muted-foreground py-12 text-center">Loading evidence…</div>}
        {detail && (
          <div className="space-y-3">
            <DrawerSection title={`Purchases (${detail.purchases.length})`}>
              {detail.purchases.length === 0 ? <div className="text-xs text-muted-foreground">No purchase linked to this session.</div> : (
                <div className="space-y-2">
                  {detail.purchases.map((purchase) => (
                    <div key={purchase.id} className="text-xs border-b border-border last:border-0 pb-2 last:pb-0">
                      <div className="flex justify-between gap-3"><span className="font-medium">{purchase.listing_title || purchase.listing_id || 'Purchase'}</span><span>{money(purchase.total_cents, purchase.currency)}</span></div>
                      <div className="text-muted-foreground">{time(purchase.created_at, tz)} · {purchase.email || 'No email'} · Ticket {purchase.ticket_id || '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </DrawerSection>

            {detail.observations.map((observation) => (
              <div key={observation.id} className="space-y-3">
                <div className="flex items-center justify-between gap-3 pt-2">
                  <h3 className="font-medium capitalize">{observation.context} observation</h3>
                  <span className="text-xs text-muted-foreground">{time(observation.last_seen_at, tz)}</span>
                </div>
                <DrawerSection title="Browser, locale, hardware and screen">
                  <FieldGrid value={{ browser: observation.signals?.browser, locale: observation.signals?.locale, hardware: observation.signals?.hardware, screen: observation.signals?.screen, preferences: observation.signals?.preferences }} />
                </DrawerSection>
                <DrawerSection title="Storage and continuity">
                  <FieldGrid value={observation.signals?.storage} />
                  <details className="mt-3 border-t border-border pt-2">
                    <summary className="text-xs cursor-pointer text-muted-foreground">Technical identifiers</summary>
                    <div className="space-y-2 mt-2">
                      <div><div className="text-[10px] text-muted-foreground">Fingerprint HMAC</div><CopyValue value={observation.fingerprint_hmac} /></div>
                      <div><div className="text-[10px] text-muted-foreground">Host storage HMAC</div><CopyValue value={observation.host_storage_hmac} /></div>
                      <div><div className="text-[10px] text-muted-foreground">Host cookie HMAC</div><CopyValue value={observation.host_cookie_hmac} /></div>
                      <div><div className="text-[10px] text-muted-foreground">Widget storage HMAC</div><CopyValue value={observation.widget_storage_hmac} /></div>
                    </div>
                  </details>
                </DrawerSection>
                <DrawerSection title="GPU and component hashes">
                  <FieldGrid value={observation.signals?.gpu} />
                  <details className="mt-3 border-t border-border pt-2">
                    <summary className="text-xs cursor-pointer text-muted-foreground">Component hashes</summary>
                    <div className="space-y-1.5 mt-2">
                      {Object.entries(observation.component_hashes || {}).map(([key, value]) => (
                        <div key={key}><div className="text-[10px] text-muted-foreground">{displayKey(key)}</div><CopyValue value={value} /></div>
                      ))}
                    </div>
                  </details>
                </DrawerSection>
                <DrawerSection title="Network, Cloudflare and TLS">
                  <FieldGrid value={{ request: observation.request_metadata, edge: observation.edge_signals }} />
                  {observation.edge_tls_hmac && <div className="mt-2"><div className="text-[10px] text-muted-foreground">Edge TLS HMAC</div><CopyValue value={observation.edge_tls_hmac} /></div>}
                </DrawerSection>
                <DrawerSection title="Timing and probe status">
                  <FieldGrid value={{ timings: observation.timings, probes: observation.probe_status, navigation: observation.signals?.navigation, automation: observation.signals?.automation }} />
                </DrawerSection>
              </div>
            ))}

            <DrawerSection title={`Related observations (${detail.related.length})`}>
              <div className="text-[11px] text-muted-foreground mb-2">Matches are listed by exact identifier type. Fingerprint matches are supporting evidence, not proof of one physical device.</div>
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {detail.related.map((row) => (
                  <div key={row.id} className="py-2 text-xs">
                    <div className="flex justify-between gap-3"><span className="capitalize">{row.context} · {row.listing_slug || row.listing_id || 'Unknown listing'}</span><span className="text-muted-foreground">{time(row.last_seen_at, tz)}</span></div>
                    <div className="font-mono text-[10px] text-muted-foreground truncate">{row.session_id}</div>
                    <div className="mt-1 flex gap-1 flex-wrap">{row.matched_by.map((kind) => <span key={kind} className="px-1 py-0.5 bg-muted rounded text-[9px]">{kind.replace(/_/g, ' ')}</span>)}</div>
                  </div>
                ))}
                {!detail.related.length && <div className="text-xs text-muted-foreground py-2">No related observations.</div>}
              </div>
            </DrawerSection>
          </div>
        )}
      </aside>
    </div>
  );
}

function PurchaseRow({ row, tz, open }) {
  const e = row.evidence || {};
  return (
    <tr className="border-t border-border hover:bg-muted/30">
      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{time(row.purchased_at, tz)}</td>
      <td className="px-3 py-2"><div>{row.email || '—'}</div><div className="font-mono text-[10px] text-muted-foreground">{compact(row.ticket_id)}</div></td>
      <td className="px-3 py-2 max-w-[15rem] truncate" title={row.listing_title || ''}>{row.listing_title || row.listing_id || '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{money(row.total_cents, row.currency)}</td>
      <td className="px-3 py-2"><button onClick={() => open(row.session_id)} disabled={!row.session_id} className="hover:opacity-80 disabled:cursor-default"><EvidenceBadges evidence={e} /></button></td>
      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><button onClick={() => open(row.session_id)} disabled={!row.session_id} className="hover:underline disabled:no-underline">{e.device_alias || '—'}</button><div className="text-[9px] text-muted-foreground">{e.continuity_basis?.replace(/_/g, ' ') || ''}</div></td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{e.related_sessions || 0} sessions · {e.related_purchases || 0} purchases</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{e.browser_os || '—'}</td>
      <td className="px-3 py-2 text-xs"><div>{e.location || '—'}</div><div className="text-[10px] text-muted-foreground">{e.organization || e.asn || ''}</div></td>
      <td className="px-3 py-2 text-xs"><FlagList flags={e.automation_flags} /></td>
    </tr>
  );
}

function ObservationRow({ row, tz, open }) {
  const e = row.evidence || {};
  return (
    <tr className="border-t border-border hover:bg-muted/30">
      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{time(row.collected_at, tz)}</td>
      <td className="px-3 py-2 capitalize">{row.context}</td>
      <td className="px-3 py-2 max-w-[13rem] truncate">{row.listing_slug || row.listing_id || 'Host page'}</td>
      <td className="px-3 py-2"><button onClick={() => open(row.session_id)} className="hover:opacity-80"><EvidenceBadges evidence={e} /></button></td>
      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><button onClick={() => open(row.session_id)} className="hover:underline">{e.device_alias || '—'}</button><div className="text-[9px] text-muted-foreground">{e.continuity_basis?.replace(/_/g, ' ') || ''}</div></td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{e.related_sessions || 0} sessions · {e.related_purchases || 0} purchases</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{e.browser_os || '—'}</td>
      <td className="px-3 py-2 text-xs"><div>{e.location || '—'}</div><div className="text-[10px] text-muted-foreground">{e.organization || e.asn || ''}</div></td>
      <td className="px-3 py-2 text-xs">{row.purchase ? <span className="text-emerald-600 dark:text-emerald-400">Purchased</span> : <span className="text-muted-foreground">Anonymous</span>}</td>
    </tr>
  );
}

function MobileCard({ row, view, tz, open }) {
  const e = row.evidence || {};
  const purchase = view === 'purchases';
  return (
    <button onClick={() => open(row.session_id)} className="w-full text-left border border-border rounded-md p-3 bg-card text-sm">
      <div className="flex justify-between gap-3 mb-1"><span className="font-medium truncate">{purchase ? row.listing_title : row.listing_slug || row.listing_id || 'Host page'}</span><span className="text-xs text-muted-foreground whitespace-nowrap">{time(purchase ? row.purchased_at : row.collected_at, tz)}</span></div>
      <EvidenceBadges evidence={e} />
      <div className="flex justify-between gap-3 mt-2 text-xs"><span className="font-mono">{e.device_alias || 'No identifier'}</span>{purchase && <span>{money(row.total_cents, row.currency)}</span>}</div>
      <div className="text-xs text-muted-foreground mt-1">{e.browser_os || 'Unknown browser'} · {e.related_sessions || 0} related sessions</div>
      {e.automation_flags?.length > 0 && <div className="mt-1"><FlagList flags={e.automation_flags} /></div>}
    </button>
  );
}

export default function DeviceEvidence({ tenantSlug }) {
  const tz = useVenueTz();
  const [days, setDays] = useState('1');
  const [view, setView] = useState('purchases');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draftQ, setDraftQ] = useState('');
  const [q, setQ] = useState('');
  const [coverage, setCoverage] = useState('all');
  const [context, setContext] = useState('all');
  const [linked, setLinked] = useState('all');
  const [edge, setEdge] = useState('all');
  const [detailSession, setDetailSession] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const params = useMemo(() => {
    const p = new URLSearchParams({ days, limit: '50' });
    if (q) p.set('q', q);
    if (view === 'purchases') p.set('coverage', coverage);
    else { p.set('context', context); p.set('linked', linked); p.set('edge', edge); }
    return p;
  }, [days, q, view, coverage, context, linked, edge]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const base = `/api/tenants/${tenantSlug}/universe-widget/device-evidence`;
        const [summaryRes, listRes] = await Promise.all([
          apiFetch(`${base}/summary?days=${days}`),
          apiFetch(`${base}/${view}?${params}`),
        ]);
        const [summaryBody, listBody] = await Promise.all([summaryRes.json(), listRes.json()]);
        if (!summaryRes.ok) throw new Error(summaryBody.error || `Summary HTTP ${summaryRes.status}`);
        if (!listRes.ok) throw new Error(listBody.error || `List HTTP ${listRes.status}`);
        if (!active) return;
        setSummary(summaryBody);
        setRows(view === 'purchases' ? listBody.purchases || [] : listBody.observations || []);
        setCursor(listBody.next_cursor || null);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [tenantSlug, days, view, params, refresh]);

  const loadMore = async () => {
    if (!cursor || moreLoading) return;
    setMoreLoading(true);
    try {
      const p = new URLSearchParams(params); p.set('cursor', cursor);
      const response = await apiFetch(`/api/tenants/${tenantSlug}/universe-widget/device-evidence/${view}?${p}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setRows((current) => [...current, ...(view === 'purchases' ? body.purchases || [] : body.observations || [])]);
      setCursor(body.next_cursor || null);
    } catch (e) { setError(e.message); }
    finally { setMoreLoading(false); }
  };

  const counts = summary?.counts || {};
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="font-semibold">Device evidence</h2>
          <div className="text-[11px] text-muted-foreground">Passive browser and Cloudflare evidence. Facts only; no risk score or automatic action.</div>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((range) => <button key={range.value} onClick={() => setDays(range.value)} className={`px-2 py-1 text-xs rounded border ${days === range.value ? 'border-primary bg-primary/10 text-primary' : 'border-input hover:bg-accent'}`}>{range.label}</button>)}
          <button onClick={() => setRefresh((n) => n + 1)} className="ml-1 px-2 py-1 text-xs border border-input rounded hover:bg-accent">Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
        <Stat label="Observations" value={Number(counts.observations || 0).toLocaleString()} sub={`${counts.host_observations || 0} host · ${counts.widget_observations || 0} widget`} />
        <Stat label="Edge coverage" value={pct(summary?.coverage?.edge_pct)} sub={`${counts.edge_observations || 0} Cloudflare enriched`} />
        <Stat label="Purchase coverage" value={pct(summary?.coverage?.purchase_any_pct)} sub={`${counts.purchases_with_any || 0} of ${counts.purchases || 0} purchases`} />
        <Stat label="Fingerprints" value={Number(counts.fingerprints || 0).toLocaleString()} sub={`${counts.repeat_fingerprints || 0} repeat groups`} />
        <Stat label="Latest" value={counts.latest_observation ? time(counts.latest_observation, tz) : '—'} sub="Last collected evidence" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex gap-1">
          {['purchases', 'observations'].map((key) => <button key={key} onClick={() => setView(key)} className={`px-3 py-1 text-xs rounded-md border capitalize ${view === key ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input hover:bg-accent'}`}>{key}</button>)}
        </div>
        <form onSubmit={(event) => { event.preventDefault(); setQ(draftQ.trim()); }} className="flex gap-1">
          <input value={draftQ} onChange={(e) => setDraftQ(e.target.value)} placeholder="Search ticket, email, session or device" className="w-64 max-w-[65vw] px-2 py-1 text-xs border border-input rounded bg-background" />
          <button className="px-2 py-1 text-xs border border-input rounded hover:bg-accent">Search</button>
        </form>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        {view === 'purchases' ? (
          <label>Coverage <select value={coverage} onChange={(e) => setCoverage(e.target.value)} className="ml-1 px-2 py-1 border border-input rounded bg-background"><option value="all">All</option><option value="full">Host + widget</option><option value="partial">Partial</option><option value="none">None</option></select></label>
        ) : <>
          <label>Context <select value={context} onChange={(e) => setContext(e.target.value)} className="ml-1 px-2 py-1 border border-input rounded bg-background"><option value="all">All</option><option value="host">Host</option><option value="widget">Widget</option></select></label>
          <label>Linkage <select value={linked} onChange={(e) => setLinked(e.target.value)} className="ml-1 px-2 py-1 border border-input rounded bg-background"><option value="all">All</option><option value="purchase">Purchased</option><option value="anonymous">Anonymous</option></select></label>
          <label>Cloudflare <select value={edge} onChange={(e) => setEdge(e.target.value)} className="ml-1 px-2 py-1 border border-input rounded bg-background"><option value="all">All</option><option value="yes">Collected</option><option value="no">Missing</option></select></label>
        </>}
      </div>

      {error && <div className="text-sm text-destructive border border-destructive/30 rounded p-3 mb-3">{error}</div>}
      {loading && !rows.length ? <div className="text-sm text-muted-foreground py-12 text-center">Loading device evidence…</div> : <>
        <div className="md:hidden space-y-2">
          {rows.map((row) => <MobileCard key={row.id} row={row} view={view} tz={tz} open={setDetailSession} />)}
        </div>
        <div className="hidden md:block border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              {view === 'purchases' ? <tr><th className="px-3 py-2">Purchased</th><th className="px-3 py-2">Buyer</th><th className="px-3 py-2">Event</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Evidence</th><th className="px-3 py-2">Device ID</th><th className="px-3 py-2">Continuity</th><th className="px-3 py-2">Browser / OS</th><th className="px-3 py-2">Location / network</th><th className="px-3 py-2">Automation</th></tr>
                : <tr><th className="px-3 py-2">Collected</th><th className="px-3 py-2">Context</th><th className="px-3 py-2">Listing</th><th className="px-3 py-2">Evidence</th><th className="px-3 py-2">Device ID</th><th className="px-3 py-2">Continuity</th><th className="px-3 py-2">Browser / OS</th><th className="px-3 py-2">Location / network</th><th className="px-3 py-2">Linkage</th></tr>}
            </thead>
            <tbody>{rows.map((row) => view === 'purchases' ? <PurchaseRow key={row.id} row={row} tz={tz} open={setDetailSession} /> : <ObservationRow key={row.id} row={row} tz={tz} open={setDetailSession} />)}</tbody>
          </table>
        </div>
        {!rows.length && <div className="text-sm text-muted-foreground py-10 text-center">No evidence matches these filters.</div>}
        {cursor && <div className="text-center mt-3"><button onClick={loadMore} disabled={moreLoading} className="px-3 py-1.5 text-xs border border-input rounded hover:bg-accent disabled:opacity-50">{moreLoading ? 'Loading…' : 'Load more'}</button></div>}
      </>}
      <EvidenceDrawer tenantSlug={tenantSlug} sessionId={detailSession} onClose={() => setDetailSession(null)} />
    </div>
  );
}
